import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import type moment from 'moment'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import SearchDatesPicker from 'components/SearchV2/Components/Mobile/DateSearchFloatingPanel/SearchDatePicker'
import ExperienceTilePrices from 'components/Experiences/ExperienceTiles/ExperienceTilePrices'
import Divider from 'LuxKit/Divider'
import GeoContext from 'contexts/geoContext'
import { CurrencyProvider } from 'contexts/currencyContext'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

const PricingAvailabilitySection = styled.div`
  scroll-margin-top: ${rem(24)};
`

const DateTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  width: ${rem(260)};
  padding: ${rem(10)} ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: 0;
  background: ${props => props.theme.palette.neutral.default.eight};
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(14)};
  color: ${props => props.theme.palette.neutral.default.one};
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${props => props.theme.palette.neutral.default.three};
  }

  &.placeholder {
    color: ${props => props.theme.palette.neutral.default.four};
  }
`

const REGION_LANGUAGE_MESSAGES = defineMessages({
  AU: {
    id: 'A6NedlstxU2peAt9JQfq',
    defaultMessage: 'Australian English',
    description: 'Language label shown in the experience pricing section for users in the Australia region',
  },
  NZ: {
    id: 'IpR2KLzsMVNgfSRINJDh',
    defaultMessage: 'New Zealand English',
    description: 'Language label shown in the experience pricing section for users in the New Zealand region',
  },
  US: {
    id: 'IIsbfYDfNwdpQk54I7gO',
    defaultMessage: 'American English',
    description: 'Language label shown in the experience pricing section for users in the United States region',
  },
  GB: {
    id: 'ICVdi7nO6FLho5mlsbdk',
    defaultMessage: 'British English',
    description: 'Language label shown in the experience pricing section for users in the United Kingdom region',
  },
  CA: {
    id: 'mRNp7G2013byl9RQJDg5',
    defaultMessage: 'Canadian English',
    description: 'Language label shown in the experience pricing section for users in the Canada region',
  },
  SG: {
    id: 'JW_F4AYagF3g1oO0dg0H',
    defaultMessage: 'English (Singapore)',
    description: 'Language label shown in the experience pricing section for users in the Singapore region',
  },
  HK: {
    id: 'rbEQcjrh7V3hRwQzcpgV',
    defaultMessage: 'English (Hong Kong)',
    description: 'Language label shown in the experience pricing section for users in the Hong Kong region',
  },
  MY: {
    id: 'xcphtqlDXpZRRpqsAdp9',
    defaultMessage: 'English (Malaysia)',
    description: 'Language label shown in the experience pricing section for users in the Malaysia region',
  },
  IN: {
    id: 'RPIcFdn7YzSxKDKKX0sy',
    defaultMessage: 'English (India)',
    description: 'Language label shown in the experience pricing section for users in the India region',
  },
  IE: {
    id: 'VjwUV-Vx5wI7EKk4ba-7',
    defaultMessage: 'English (Ireland)',
    description: 'Language label shown in the experience pricing section for users in the Ireland region',
  },
  default: {
    id: 'PL4XgqSkziE8-ttnsBhE',
    defaultMessage: 'English',
    description: 'Fallback language label shown in the experience pricing section when no region-specific variant exists',
  },
})

const CALENDAR_MONTHS_AHEAD = 12

interface Props {
  price?: number
  memberPrice?: number
  memberDiscountPercent?: number
  value?: number
  currencyCode: string
  deeplinkUrl: string
  isSoldOut: boolean
}

function ExperienceDetailPricing({ price, memberPrice, memberDiscountPercent, value, currencyCode, deeplinkUrl, isSoldOut }: Props) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const geo = useContext(GeoContext)
  const pricingSectionRef = useRef<HTMLDivElement>(null)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<moment.Moment | undefined>(undefined)
  const href = `/${geo.currentRegionCode}${deeplinkUrl}`

  const hasDiscount = value !== undefined && price !== undefined && value > price
  const discountPercent = hasDiscount ? Math.round(((value - price) / value) * 100) : 0

  const language = intl.formatMessage(
    REGION_LANGUAGE_MESSAGES[geo.currentRegionCode as keyof typeof REGION_LANGUAGE_MESSAGES] ?? REGION_LANGUAGE_MESSAGES.default,
  )

  const today = useMemo(() => new Date(), [])
  const calendarMaxDate = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + CALENDAR_MONTHS_AHEAD)
    return d
  }, [])

  const dateDisplayLabel = selectedDate
    ? dateFormatter(selectedDate, INTL_DMY_CASUAL_SHORT_FORMAT)
    : intl.formatMessage({
        id: '8jkd_8P8nP46teBtG7ps',
        defaultMessage: 'Select a date',
        description: 'Placeholder text for the date input in experience pricing section',
      })

  const checkAvailabilityLabel = intl.formatMessage({
    id: 'cz6O9A05fQXwuzafKdDO',
    defaultMessage: 'Check availability',
    description: 'Button label to scroll to pricing and availability section for experience',
  })

  const bookNowLabel = intl.formatMessage({
    id: 'azlPTkuZgo66yhqZs2e4',
    defaultMessage: 'Book now',
    description: 'Button label to book an experience offer on the experience detail drawer',
  })

  const soldOutLabel = intl.formatMessage({
    id: 'r1Z0H_THzeGe1u2EN68c',
    defaultMessage: 'Sold out',
    description: 'Label shown when experience offer is sold out on the experience detail drawer',
  })

  const pricingHeading = intl.formatMessage({
    id: 'u_VwtpzWYhCsiddc0cPX',
    defaultMessage: 'Pricing & availability',
    description: 'Section heading for experience pricing and availability',
  })

  const availabilityPrompt = intl.formatMessage({
    id: 'fzel93h415sJtLJXIEHH',
    defaultMessage: 'To check availability and prices, please let us know the following:',
    description: 'Prompt text in the pricing and availability card for experiences',
  })

  const dateLabel = intl.formatMessage({
    id: '1Aagpy11pk_faC-08D-V',
    defaultMessage: 'Date',
    description: 'Date field label in experience pricing availability section',
  })

  const languageLabel = intl.formatMessage({
    id: 'Kw83v7a_V0QHVCdC9A4L',
    defaultMessage: 'Language',
    description: 'Language field label in experience pricing availability section',
  })

  const handleCheckAvailability = useCallback(() => {
    pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const handleDateToggle = useCallback(() => setIsCalendarOpen(prev => !prev), [])

  const handleDatesChange = useCallback(({ startDate: newDate }: { startDate: moment.Moment; endDate?: moment.Moment | null }) => {
    setSelectedDate(newDate)
    setIsCalendarOpen(false)
  }, [])

  return (
    <CurrencyProvider currency={currencyCode}>
      <Group direction="vertical" gap={24}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
          <Group direction="vertical" gap={4}>
            {price !== undefined && (
              <ExperienceTilePrices
                totalPrice={price}
                totalMemberPrice={memberPrice}
                totalGrossPrice={value}
                discountPercentage={discountPercent}
                discountMemberPercentage={memberDiscountPercent}
                isFromPrice
                ticketUnitLabel="person"
                size="L"
              />
            )}
          </Group>
          <TextButton
            kind="primary"
            size="medium"
            onClick={handleCheckAvailability}
            disabled={isSoldOut}
          >
            {isSoldOut ? soldOutLabel : checkAvailabilityLabel}
          </TextButton>
        </Group>

        <Divider kind="secondary" />

        <Group direction="vertical" gap={8}>
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'pYLZeh0rjjWTE-Ak6xb9',
              defaultMessage: 'Why it fits your trip',
              description: 'Section heading for why the experience fits the user trip',
            })}
          </Heading>
          <BodyText variant="medium" colour="neutral-two">
            {intl.formatMessage({
              id: '-CHvPsdyvAZP0hXoB9hn',
              defaultMessage: "This experience is a great match for your trip — it's conveniently located, highly rated by travellers, and offers a memorable way to explore the destination. Whether you're after culture, adventure, or relaxation, this activity adds something special to your itinerary.",
              description: 'Generic description explaining why the experience fits the user trip',
            })}
          </BodyText>
        </Group>

        <Divider kind="secondary" />

        <PricingAvailabilitySection ref={pricingSectionRef}>
          <Group direction="vertical" gap={16}>
            <Heading variant="heading5">{pricingHeading}</Heading>

            <Sheet padding={20} border="neutral-five" borderRadius="M">
              <Group direction="vertical" gap={16}>
                <BodyText variant="medium">{availabilityPrompt}</BodyText>
                <Group direction="horizontal" gap={24} verticalAlign="end">
                  <Group direction="vertical" gap={4}>
                    <Caption variant="large" weight="bold">{dateLabel}</Caption>
                    <DateTrigger
                      type="button"
                      onClick={handleDateToggle}
                      className={selectedDate ? undefined : 'placeholder'}
                    >
                      <LineCalendarIcon size="S" />
                      {dateDisplayLabel}
                    </DateTrigger>
                  </Group>
                  <Group direction="vertical" gap={4}>
                    <Caption variant="large" weight="bold">{languageLabel}</Caption>
                    <BodyText variant="medium">{language}</BodyText>
                  </Group>
                </Group>
                {isCalendarOpen && (
                  <SearchDatesPicker
                    type="single"
                    onDatesChange={handleDatesChange}
                    startDate={selectedDate}
                    minDate={today}
                    maxDate={calendarMaxDate}
                  />
                )}
              </Group>
            </Sheet>

            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
              <Group direction="vertical" gap={4}>
                {price !== undefined && (
                  <ExperienceTilePrices
                    totalPrice={price}
                    totalMemberPrice={memberPrice}
                    totalGrossPrice={value}
                    discountPercentage={discountPercent}
                    isFromPrice
                    ticketUnitLabel="person"
                    size="L"
                  />
                )}
              </Group>
              <TextButton
                kind="primary"
                size="medium"
                endIcon={<LineExternalLinkAltIcon />}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                disabled={isSoldOut}
              >
                {isSoldOut ? soldOutLabel : bookNowLabel}
              </TextButton>
            </Group>
          </Group>
        </PricingAvailabilitySection>
      </Group>
    </CurrencyProvider>
  )
}

export default ExperienceDetailPricing
